import React, { useContext, useState } from "react";
import { DateTime } from "luxon";
import Image from "next/image";
import Link from "next/link";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { HeartFilled, HeartOutline } from "components/atoms/Icon";
import NestedComments from "components/molecules/NestedComments";
import ReplySection from "components/molecules/ReplySection";
import { likeComment, updateComment } from "lib/actions";
import { AppContext } from "lib/contexts";
import { ICommentDTO, ImageDTO } from "types";
import { Span } from "utils/styling/typography.styled";
import * as Styled from "./Comment.styled";
interface CommentProps {
  comment: ICommentDTO;
  image: ImageDTO | null;
  onDelete: (commentId: string) => void;
  nestingLevel?: number;
}
const Comment: React.FC<CommentProps> = ({
  comment,
  image,
  onDelete,
  nestingLevel = 0
}) => {
  const {
    state
  } = useContext(AppContext);
  const [likes, setLikes] = useState(comment.likes);
  const [likedByUser, setLikedByUser] = useState(comment.likedByCurrentUser);
  const [showReplySection, setShowReplySection] = useState(false);
  const [showEditSection, setShowEditSection] = useState(false);
  const [replies, setReplies] = useState<ICommentDTO[]>(comment.replies || []);
  const [newContent, setNewContent] = useState(comment.content);
  const [isEdited, setIsEdited] = useState(comment.edited);
  const handleLike = async () => {
    const response = await likeComment(comment.id);
    if (response.comment) {
      setLikes(response.comment.likes);
      setLikedByUser(response.comment.likedByCurrentUser);
    } else if (response.error) {
      console.error("Failed to like comment:", response.error);
    }
  };
  const handleNewReply = (newComment: ICommentDTO) => {
    setReplies(prevReplies => [...prevReplies, newComment]);
    setShowReplySection(false); // Hide the ReplySection after adding a new reply
  };
  const handleDelete = () => {
    onDelete(comment.id);
  };
  const handleUpdate = async () => {
    const response = await updateComment(comment.id, newContent);
    if (response.comment) {
      setNewContent(response.comment.content);
      setShowEditSection(false);
      setIsEdited(true);
    } else if (response.error) {
      console.error("Failed to update comment:", response.error);
    }
  };
  const handleNestedDelete = (commentId: string) => {
    setReplies(prevReplies => prevReplies.filter(reply => reply.id !== commentId));
  };
  const isOwnerOrImageOwner = comment.user.username === state.username || (image?.imageMetadata.username ?? "") === state.username;
  const formatDate = (dateString: string) => {
    const date = DateTime.fromISO(dateString, {
      zone: "utc"
    });
    const now = DateTime.now().setZone("utc");
    const diffInSeconds = now.diff(date, "seconds").seconds;
    if (diffInSeconds < 60) {
      return `${Math.floor(diffInSeconds)} seconds ago`;
    } else if (diffInSeconds < 3600) {
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    } else if (diffInSeconds < 86400) {
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    } else {
      return date.toFormat("MMM dd, yyyy");
    }
  };
  return <Styled.CommentContainer data-sentry-element="unknown" data-sentry-component="Comment" data-sentry-source-file="index.tsx">
            <Styled.CommentHeader data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Link title={comment.user.username} href={`/profile/${comment.user.username}`} passHref data-sentry-element="Link" data-sentry-source-file="index.tsx">
                    <Styled.Username data-sentry-element="unknown" data-sentry-source-file="index.tsx">{comment.user.username}</Styled.Username>
                </Link>
                <Styled.Timestamp suppressHydrationWarning data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {comment.updatedAt ? formatDate(comment.updatedAt) : formatDate(comment.createdAt)}
                    {isEdited && <Styled.EditedLabel>(Edited)</Styled.EditedLabel>}
                </Styled.Timestamp>
                <Styled.ReactContainer data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    <Styled.Icon key={comment.id} onClick={() => handleLike()} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                        <Styled.HeartCheckbox data-sentry-element="unknown" data-sentry-source-file="index.tsx" />
                        <Styled.IconWrapper data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                            {likedByUser ? <Image height={24} width={24} priority src={HeartFilled} alt="Heart" /> : <Image height={24} width={24} priority src={HeartOutline} alt="Heart" />}
                        </Styled.IconWrapper>
                    </Styled.Icon>
                    <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">{likes}</Span>
                </Styled.ReactContainer>
            </Styled.CommentHeader>
            {showEditSection ? <Styled.EditSection>
                    <Styled.TextArea value={newContent} onChange={e => {
        e.stopPropagation();
        setNewContent(e.target.value);
      }} onKeyDown={e => {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          e.stopPropagation();
        }
      }} placeholder="Edit your comment..." required />
                    <Button variant={ButtonType.SUCCESS} onClick={handleUpdate}>
                        Submit
                    </Button>
                </Styled.EditSection> : <Styled.CommentText>{newContent}</Styled.CommentText>}{" "}
            {comment.attachmentUrl && <Image src={comment.attachmentUrl} alt="Attachment" width={400} height={400} layout="responsive" />}
            <Styled.ActionButtons data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                <Styled.InlineButton onClick={() => setShowReplySection(!showReplySection)} data-sentry-element="unknown" data-sentry-source-file="index.tsx">
                    {showReplySection ? "Cancel" : <strong>Reply</strong>}
                </Styled.InlineButton>
                {isOwnerOrImageOwner && <>
                        <Styled.InlineButton onClick={() => setShowEditSection(!showEditSection)}>
                            {showEditSection ? "Cancel" : "Edit"}
                        </Styled.InlineButton>
                        <Styled.InlineButton onClick={handleDelete}>
                            Delete
                        </Styled.InlineButton>
                    </>}
            </Styled.ActionButtons>
            {showReplySection && <ReplySection parentId={comment.id} imageId={image?.id || ""} onNewComment={handleNewReply} />}
            {nestingLevel < 2 ? <NestedComments replies={replies} image={image || null} onDelete={handleNestedDelete} nestingLevel={nestingLevel + 1} /> : replies.map(reply => <Comment key={reply.id} comment={reply} image={image} onDelete={handleNestedDelete} nestingLevel={nestingLevel + 1} />)}
        </Styled.CommentContainer>;
};
export default Comment;